import { ReactNode, useMemo, useContext, useEffect, useState } from "react";
import {
  BrowserRouter as Router,
  Route,
  Switch,
  useParams,
  useLocation,
  matchPath
} from "react-router-dom";
import { ApolloProvider } from "@apollo/client";

import {
  LicenseManager,
  AllEnterpriseModule,
  ModuleRegistry,
  provideGlobalGridOptions,
  ValidationModule,
  setupAgTestIds
} from "ag-grid-enterprise";
import {
  Login,
  HomePage,
  ProjectsListpage,
  // ProjectCreatePage,
  SubmittalDetailspage,
  Users,
  NotFoundPage,
  ProjectDashboardPage,
  RiskReportPage,
  DesignPage,
  SubscriptionReports,
  MaterialFeatureLogPage
} from "pages";
import PrivateRoute from "ProtectedRoute";
import ForgetPassword from "pages/forgetpassword";
import SetupAccount from "pages/setup_account";
import SetPassword from "pages/setpassword";
import ChangePassword from "pages/changepassword";
import CompanyList from "pages/companies";
import ProjectProvider, { ProjectContext } from "context/ProjectProvider";
import SubscriptionProvider, {
  SubscriptionContext,
  RoleSubscriptionEnum
} from "context/SubscriptionProvider";
import AcceptInvitePage from "pages/accept-invite/acceptInvite";
import CreateProjectPage from "pages/project-create/create-project";
import { Navbar } from "components";

import "./ag-grid-override.scss";
import "./app.scss";

import { AppContext } from "context/AppProvider";
import { useGetGQLClientForSubscription } from "hooks/init-gql-client";
import PartnerApp from "partner-app/Partner-App";
import { AG_GRID_LICENSE } from "config/config";
import TinyUrlRedirection from "pages/tiny-url";
import SubscriptionSettingsPage from "pages/subscription-settings";
import { updateGAConfigWithUserId } from "utils/analytics";
import ProjectSettingManu from "pages/project-setting-menu";
import AdminApp from "admin-app/admin-app";
import { setAccessTokenAndSubscriptionId } from "services/auth";
import RiskReportExportPDF from "pages/risk-report/risk-report-export-pdf";
import ReportsPageManu from "pages/reports-menu";
import UpcomingSubmittals from "pages/reports-menu/upcoming-workflows";

import PreconstructionDetailsPage from "pages/preconstruction-details";
import ScheduleActivitiesReport from "pages/reports-menu/schedule-activities-report";
import PreconstructionLogPage from "pages/preconstruction-log-page";
import DesignPackageLogPage from "pages/design-package-log-page";
import DesignPackageDetailPage from "pages/design-package-details-page";
import ProjectScheduleCompare from "pages/schedule/schedule-page/compare-schedule-2";
import ProjectSchedules from "pages/schedule/schedule-page/project-schedule";
import CIQTabs from "components/ciq-tabs/ciq-tabs";
import ScheduleMenuTabs from "pages/schedule-manu-tabs/schedule-manu-tabs";
import MaterialPlanning from "pages/material-planning";
import InAppUpcomingMilestonesReport from "pages/reports-menu/upcoming-milestones/in-app-report/in-app-upcoming-milestones-report";
import ChangePasswordOnExpiry from "pages/changepassword/change-password-on-expiry";

import {
  MaterialCiqIcon,
  ScheduleCiqIcon,
  SubmittalCiqIcon
} from "components/svg-icons";
import ProjectDashboardNavBar from "components/navbar/project-dashboard-nav-bar";
import SubmittalLogLandingPage from "pages/submittal-log/submittal-log-landing-page";
import FeatureMaterialDetailsLandingPage from "pages/material-details/material-details-landing-page";
import FeatureScheduleDetailsLandingPage from "pages/schedule/schedule-page/project-schedule/project-schedule-details/schedule-details-landing-page";
import FeatureMaterialLinking from "pages/feature-entity-linking-page/feature-material";
import FeatureSubmittalLinking from "pages/feature-entity-linking-page/feature-submittal";
import useCheckVersionUpdates from "hooks/check-version-updates";
import MaterialExtractedLogPage from "pages/material-extracted-log";
import CustomReportPage from "pages/reports-menu/feature-custom-reports/custom-report-page";
import SSOLogin from "pages/sso-login/sso-login";
import SSOCallback from "pages/sso-login/sso-callback";
import SubscriptionSelect from "pages/subscription-select/subscription-select";
import AccountLocked from "pages/login/account-locked";
import { Spin } from "antd";
import useValidateRouteAccess from "hooks/validate-route-access";
import useValidateSubscriptionRouteAccess from "hooks/validate-subscription-route-access";
import { PartnerBaseRoute } from "./constants";

LicenseManager.setLicenseKey(AG_GRID_LICENSE);
// Register all enterprise features
ModuleRegistry.registerModules([AllEnterpriseModule]);

// via process.env.NODE_ENV
if (process.env.NODE_ENV !== "production") {
  ModuleRegistry.registerModules([ValidationModule]);
}

setupAgTestIds();

// Mark all grids as using legacy themes
provideGlobalGridOptions({
  theme: "legacy",
  columnMenu: "legacy",
  headerHeight: 36,
  tooltipShowMode: "whenTruncated",
  enableFilterHandlers: true
});

// TODO: make routing config for better route management
type LayoutProps = {
  children: ReactNode;
};

function NoMenuLayout(props: LayoutProps) {
  const { children } = props;
  return (
    <div className="flex flex-col">
      <Navbar />
      {children}
    </div>
  );
}

function HomeLayout(props: LayoutProps) {
  const { children } = props;
  const {
    isFeatureFlagEnabled: isFeatureFlagSubscriptionEnabled,
    subscriptionRole
  } = useContext(SubscriptionContext);
  const isUsageDashboardEnabled = isFeatureFlagSubscriptionEnabled(
    "USAGE_SUMMARY_DASHBOARD"
  );
  const isExecutiveOverviewDashboardEnabled = isFeatureFlagSubscriptionEnabled(
    "EXECUTIVE_OVERVIEW_DASHBOARD"
  );

  const isValidRoute = useValidateSubscriptionRouteAccess({
    subscriptionRole
  });

  const menuLinks = useMemo(() => {
    const links = [
      {
        label: "Users",
        key: "/users"
      },
      {
        label: "Companies",
        key: "/companies"
      },
      {
        label: "Projects",
        key: "/projects"
      },
      {
        label: "Settings",
        key: "/settings"
      }
    ];

    if (isUsageDashboardEnabled || isExecutiveOverviewDashboardEnabled) {
      links.splice(3, 0, {
        label: "Analytics",
        key: "/analytics"
      });
    }

    return links;
  }, [isUsageDashboardEnabled, isExecutiveOverviewDashboardEnabled]);

  const renderContent = () => {
    if (
      subscriptionRole === undefined ||
      subscriptionRole === RoleSubscriptionEnum.none
    ) {
      return (
        <div className="flex items-center justify-center h-full">
          <Spin size="large" />
        </div>
      );
    }

    if (!isValidRoute) {
      return <NotFoundPage />;
    }

    return (
      <div className="flex flex-col h-screen">
        <Navbar menuLinks={menuLinks} />
        {children}
      </div>
    );
  };

  return renderContent();
}

function ProjectLayout(props: LayoutProps) {
  const { children } = props;
  const { projectId } = useParams<{ projectId: string }>();

  const { pathname } = useLocation();

  const menuLinks = useMemo(
    () => [
      {
        label: (
          <div className="flex items-center space-x-2">
            <SubmittalCiqIcon className="fill-inherit" size={14} opacity="1" />
            <div>Submittals</div>
          </div>
        ),
        key: `/project/${projectId}/submittals`
      },
      {
        label: (
          <div className="flex items-center space-x-2 ">
            <MaterialCiqIcon className="fill-inherit" size={14} opacity="1" />
            <div>Materials</div>
          </div>
        ),
        key: `/project/${projectId}/materials`
      },
      {
        label: (
          <div className="flex items-center space-x-2">
            <ScheduleCiqIcon className="fill-inherit" size={14} opacity="1" />
            <div>Schedules</div>
          </div>
        ),
        key: `/project/${projectId}/schedule`
      },
      {
        label: "Reports",
        key: `/project/${projectId}/reports`
      },
      {
        label: "Settings",
        key: `/project/${projectId}/settings`
      }
    ],
    [projectId]
  );
  const projectContext = useContext(ProjectContext);
  const userRole = projectContext?.tokenContents?.role;
  const isValidRoute = useValidateRouteAccess({ userRole });
  const renderContent = () => {
    if (!userRole) {
      return (
        <div className="flex items-center justify-center h-full">
          <Spin size="large" />
        </div>
      );
    }

    if (!isValidRoute) {
      return <NotFoundPage />;
    }

    return (
      <div className="flex flex-col h-screen">
        {matchPath(pathname, { path: `/project/${projectId}/dashboard` }) ? (
          <ProjectDashboardNavBar />
        ) : (
          <Navbar menuLinks={menuLinks} />
        )}
        <div className="grow overflow-auto">{children}</div>
      </div>
    );
  };

  return renderContent();
}

function ProjectScheduleLayout(props: LayoutProps) {
  const { children } = props;
  const { projectId } = useParams<{ projectId: string }>();

  const menuLinks = useMemo(
    () => [
      {
        label: "Project Schedule",
        key: `/project/${projectId}/schedule/project-schedules`
      },
      {
        label: "Change Impact",
        key: `/project/${projectId}/schedule/change-impact`
      },
      {
        label: "Submittal Linking",
        key: `/project/${projectId}/schedule/submittal-linking`
      },
      {
        label: "Material Linking",
        key: `/project/${projectId}/schedule/material-linking`
      }
    ],
    [projectId]
  );

  return (
    <div className="flex flex-col h-full gap-y-2">
      <CIQTabs menuLinks={menuLinks} key={projectId} />
      {children}
    </div>
  );
}

function PrivateRoutes() {
  updateGAConfigWithUserId();
  const { gqlClient } = useGetGQLClientForSubscription();
  if (!gqlClient) return <PrivateRoute />;
  return (
    <ApolloProvider client={gqlClient}>
      <SubscriptionProvider>
        <Switch>
          <PrivateRoute
            exact
            path={["/home", "/new-project", "/change_password"]}
          >
            <NoMenuLayout>
              <PrivateRoute exact path="/home" component={HomePage} />
              <PrivateRoute
                exact
                path="/new-project"
                component={CreateProjectPage}
              />
              <PrivateRoute
                exact
                path="/change_password"
                component={ChangePassword}
              />
            </NoMenuLayout>
          </PrivateRoute>
          <PrivateRoute
            exact
            path={[
              "/users",
              "/companies",
              "/projects",
              "/analytics",
              "/settings",
              "/settings/:tab1Id",
              "/settings/:tab1Id/:tab2Id",
              "/settings/:tab1Id/:tab2Id/:tab3Id"
            ]}
          >
            <HomeLayout>
              <PrivateRoute exact path="/users" component={Users} />
              <PrivateRoute exact path="/companies" component={CompanyList} />
              <PrivateRoute
                exact
                path="/projects"
                component={ProjectsListpage}
              />
              <PrivateRoute
                exact
                path="/analytics"
                component={SubscriptionReports}
              />
              <PrivateRoute
                exact
                path={[
                  "/settings",
                  "/settings/:tab1Id",
                  "/settings/:tab1Id/:tab2Id",
                  "/settings/:tab1Id/:tab2Id/:tab3Id"
                ]}
                component={SubscriptionSettingsPage}
              />
            </HomeLayout>
          </PrivateRoute>
          <PrivateRoute
            path="/project/:projectId/reports/submittals/export"
            exact
          >
            <ProjectProvider>
              <PrivateRoute
                exact
                path="/project/:projectId/reports/submittals/export"
                component={RiskReportExportPDF}
              />
            </ProjectProvider>
          </PrivateRoute>
          <PrivateRoute path={["/project/:projectId"]}>
            <ProjectProvider>
              <ProjectLayout>
                <Switch>
                  <PrivateRoute
                    exact
                    path="/project/:projectId/dashboard"
                    component={ProjectDashboardPage}
                  />
                  <PrivateRoute
                    exact
                    path="/project/:projectId/design"
                    component={DesignPage}
                  />
                  {/* <PrivateRoute
                    exact
                    path="/project/:projectId/:logPageType"
                    component={EntityLogPage}
                  /> */}
                  <PrivateRoute
                    exact
                    path="/project/:projectId/submittals"
                    component={SubmittalLogLandingPage}
                  />
                  <PrivateRoute
                    exact
                    path="/project/:projectId/submittals/:submittalId"
                    component={SubmittalDetailspage}
                  />
                  <PrivateRoute
                    exact
                    path="/project/:projectId/bid-packages"
                    component={PreconstructionLogPage}
                  />
                  <PrivateRoute
                    exact
                    path="/project/:projectId/bid-packages/:bidPackageId"
                    component={PreconstructionDetailsPage}
                  />
                  <PrivateRoute
                    exact
                    path="/project/:projectId/design-packages"
                    component={DesignPackageLogPage}
                  />
                  <PrivateRoute
                    exact
                    path="/project/:projectId/design-packages/:designPackageId"
                    component={DesignPackageDetailPage}
                  />
                  <PrivateRoute
                    exact
                    path={[
                      "/project/:projectId/settings",
                      "/project/:projectId/settings/:tab1Id",
                      "/project/:projectId/settings/:tab1Id/:tab2Id",
                      "/project/:projectId/settings/:tab1Id/:tab2Id/:tab3Id"
                    ]}
                    component={ProjectSettingManu}
                  />
                  <PrivateRoute
                    exact
                    path="/project/:projectId/materials"
                    component={MaterialFeatureLogPage}
                  />
                  <PrivateRoute
                    exact
                    path="/project/:projectId/materials/extracted"
                    component={MaterialExtractedLogPage}
                  />
                  <PrivateRoute
                    exact
                    path="/project/:projectId/materials/:materialId"
                    component={FeatureMaterialDetailsLandingPage}
                  />

                  <PrivateRoute
                    exact
                    path="/project/:projectId/schedule"
                    component={ScheduleMenuTabs}
                  />

                  <PrivateRoute
                    exact
                    path="/project/:projectId/reports"
                    component={ReportsPageManu}
                  />
                  <PrivateRoute
                    exact
                    path="/project/:projectId/reports/risk"
                    component={RiskReportPage}
                  />
                  <PrivateRoute
                    path="/project/:projectId/reports/upcoming"
                    component={UpcomingSubmittals}
                  />
                  <PrivateRoute
                    path="/project/:projectId/reports/activities"
                    component={ScheduleActivitiesReport}
                  />
                  <PrivateRoute
                    path="/project/:projectId/reports/procurement-follow-ups"
                    component={InAppUpcomingMilestonesReport}
                  />
                  <PrivateRoute
                    path="/project/:projectId/reports/custom-report/:reportId"
                    component={CustomReportPage}
                  />

                  <ProjectScheduleLayout>
                    <PrivateRoute
                      exact
                      path="/project/:projectId/schedule/project-schedules"
                      component={ProjectSchedules}
                    />
                    <PrivateRoute
                      exact
                      path="/project/:projectId/schedule/project-schedule/:selectedVersionId"
                      component={FeatureScheduleDetailsLandingPage}
                    />
                    <PrivateRoute
                      exact
                      path="/project/:projectId/schedule/change-impact"
                      component={ProjectScheduleCompare}
                    />

                    <PrivateRoute
                      exact
                      path="/project/:projectId/schedule/submittal-linking"
                      component={FeatureSubmittalLinking}
                    />
                    <PrivateRoute
                      exact
                      path="/project/:projectId/schedule/material-linking"
                      component={FeatureMaterialLinking}
                    />
                  </ProjectScheduleLayout>
                  <PrivateRoute>
                    <Route component={NotFoundPage} />
                  </PrivateRoute>
                </Switch>
              </ProjectLayout>
            </ProjectProvider>
          </PrivateRoute>
          <PrivateRoute>
            <Route component={NotFoundPage} />
          </PrivateRoute>
        </Switch>
      </SubscriptionProvider>
    </ApolloProvider>
  );
}

function App() {
  const [isTokenLoaded, setIsTokenLoaded] = useState(false);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const code = params.get("code");

    if (code) {
      setAccessTokenAndSubscriptionId().finally(() => {
        setIsTokenLoaded(true);
      });
    } else {
      setIsTokenLoaded(true);
    }
  }, []);

  useCheckVersionUpdates({ autoCheckInIntervals: true });
  const { inIframe }: any = useContext(AppContext);

  if (!isTokenLoaded) {
    return null;
  }

  return !inIframe ? (
    <Router>
      <Switch>
        <Route exact path="/" component={SSOLogin} />
        <Route exact path="/Login" component={Login} />
        <Route exact path="/callback" component={SSOCallback} />
        <Route
          exact
          path="/subscription-select"
          component={SubscriptionSelect}
        />
        <Route exact path="/set_password" component={SetupAccount} />
        <Route exact path="/reset_password" component={SetPassword} />
        <Route exact path="/account-locked" component={AccountLocked} />
        <Route exact path="/forgot_password" component={ForgetPassword} />
        <Route
          exact
          path="/reset_expired_password"
          component={ChangePasswordOnExpiry}
        />
        <Route exact path="/accept-invite" component={AcceptInvitePage} />
        <Route exact path="/io/:projectId" component={TinyUrlRedirection} />
        <Route exact path="/submit-lead-time" component={MaterialPlanning} />

        <Route exact path="/admin">
          <AdminApp />
        </Route>

        <Route exact path={PartnerBaseRoute}>
          <PartnerApp />
        </Route>
        <Route component={PrivateRoutes} />
      </Switch>
    </Router>
  ) : (
    <Router>
      <Switch>
        <Route exact path="/io/:projectId" component={TinyUrlRedirection} />
        <Route exact path={PartnerBaseRoute}>
          <PartnerApp />
        </Route>
        <PrivateRoute>
          <Route component={NotFoundPage} />
        </PrivateRoute>
      </Switch>
    </Router>
  );
}

// TODO: Need to move to a separate definition file
declare global {
  interface Window {
    JSREPORT_READY_TO_START: any; // this will be your variable name
  }
}

export default App;
